import { useState, useCallback, useEffect, useRef } from 'react'
import useAuthStore from '../store/authStore.js'
import type { RecentProduction } from '../types/domain'

const BASE_KEY = 'recent-productions'
const MAX = 5

function storageKey(tenantSlug: string | null | undefined): string {
  return tenantSlug ? `${BASE_KEY}:${tenantSlug}` : BASE_KEY
}

function loadFromStorage(key: string): RecentProduction[] {
  try { return JSON.parse(localStorage.getItem(key) as string) ?? [] } catch { return [] }
}

export interface UseRecentProductionsResult {
  recents: RecentProduction[]
  track: (production: RecentProduction) => void
}

export default function useRecentProductions(): UseRecentProductionsResult {
  const tenantSlug = useAuthStore(s => s.tenant?.slug)
  const key = storageKey(tenantSlug)

  const [recents, setRecents] = useState<RecentProduction[]>(() => loadFromStorage(key))

  // Re-load from the new tenant's storage only when the key actually changes
  // (i.e. tenant switch or impersonation). Skip initial mount — useState already
  // loaded the correct value, and calling setRecents with a new [] reference on
  // mount would cause a spurious re-render that consumes mockReturnValueOnce in tests.
  const isMountRef = useRef(true)
  useEffect(() => {
    if (isMountRef.current) { isMountRef.current = false; return }
    setRecents(loadFromStorage(key))
  }, [key])

  const track = useCallback((production: RecentProduction) => {
    setRecents(prev => {
      const filtered = prev.filter(p => p.id !== production.id)
      const next = [production, ...filtered].slice(0, MAX)
      try { localStorage.setItem(key, JSON.stringify(next)) } catch { /* localStorage unavailable */ }
      return next
    })
  }, [key])

  return { recents, track }
}
